import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { usePermissions } from '@/api/permissions';
import { canOpen } from '@/lib/permissions';
import { desktopOnlyArea } from '@/lib/navigation';
import { DesktopNotice, useShowDesktopPage } from '@/components/dashboard/DesktopOnly';
import { ROUTES } from '@/lib/routes';

/**
 * RBAC page guard (dev/planning/general.md §4.12): a page the user's role can't open shows "No access" instead of a
 * page full of 403s. Which area opens which path: `PAGE_RULES` in lib/permissions.ts. The server enforces regardless.
 */
export function RouteGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const perms = usePermissions();
  // Phones: desktop-only pages show a notice instead of mounting (their queries never run). Mobile.md §2.3.
  const [showDesktop, openAnyway] = useShowDesktopPage();
  const desktopArea = desktopOnlyArea(pathname);
  if (canOpen(perms, pathname)) {
    if (desktopArea && !showDesktop) return <DesktopNotice area={desktopArea} onOpenAnyway={openAnyway} />;
    return <>{children}</>;
  }
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-20 text-center text-sm">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted"><Lock className="h-5 w-5 text-muted-foreground" /></span>
      <p className="font-medium text-foreground">You don't have access to this page</p>
      <p className="text-muted-foreground">
        Your role{perms ? <> (<b className="text-foreground">{perms.roleName}</b>)</> : null} doesn't include it. Ask a Cosmos admin if you need it.
      </p>
      <Link to={ROUTES.home} className="text-primary hover:underline">Go to Home</Link>
    </div>
  );
}
